import React, { useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { items } from "../data/items";
import { kastConfigs } from "../data/kastConfig";
import { Item, BoxItem } from "../types";
import ItemList from "./ItemList";
import KastGrid from "./KastGrid";
import SelectedItemsList from "./SelectedItemsList";
import PrintLayout from "./PrintLayout";

const KastPage: React.FC = () => {
  const { size } = useParams<{ size: string }>();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [boxItems, setBoxItems] = useState<BoxItem[]>([]);
  const [usedItems, setUsedItems] = useState<Set<string>>(new Set());

  const kastConfig = kastConfigs[size || "kleine"];

  const handleItemSelect = useCallback(
    (item: Item) => {
      if (!usedItems.has(item.id)) {
        setSelectedItem(item);
      }
    },
    [usedItems]
  );

  const handleBoxClick = useCallback(
    (boxIndex: number) => {
      if (selectedItem) {
        // Check if this box already has an item
        const existingItem = boxItems.find((bi) => bi.boxIndex === boxIndex);
        if (existingItem) {
          // Remove item from box
          setBoxItems((prev) => prev.filter((bi) => bi.boxIndex !== boxIndex));
          setUsedItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(existingItem.itemId);
            return newSet;
          });
        } else {
          // Add item to box
          setBoxItems((prev) => [
            ...prev,
            { itemId: selectedItem.id, boxIndex },
          ]);
          setUsedItems((prev) => {
            const newSet = new Set(prev);
            newSet.add(selectedItem.id);
            return newSet;
          });
          setSelectedItem(null);
        }
      }
    },
    [selectedItem, boxItems]
  );

  const handleItemRemove = useCallback((itemId: string) => {
    setBoxItems((prev) => prev.filter((bi) => bi.itemId !== itemId));
    setUsedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);

  const getItemById = (id: string) => items.find((item) => item.id === id);

  const availableItems = items.filter((item) => !usedItems.has(item.id));

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {kastConfig.label}
            </h1>
            <div className="flex space-x-2">
              <button
                onClick={handlePrint}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Print Layout
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Terug naar start
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Item List */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Beschikbare items
              </h2>
              <ItemList
                items={availableItems}
                selectedItem={selectedItem}
                onItemSelect={handleItemSelect}
              />
            </div>
          </div>

          {/* Right Side - Kast Grid */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Kast Layout ({kastConfig.boxCount} vakken)
              </h2>
              <KastGrid
                boxCount={kastConfig.boxCount}
                boxItems={boxItems}
                onBoxClick={handleBoxClick}
                getItemById={getItemById}
              />
            </div>
          </div>
        </div>

        {/* Bottom - Selected Items List */}
        <div className="mt-8">
          <SelectedItemsList
            boxItems={boxItems}
            getItemById={getItemById}
            onItemRemove={handleItemRemove}
          />
        </div>
      </div>

      {/* Print Layout (hidden by default, shown only when printing) */}
      <div className="hidden">
        <PrintLayout
          boxCount={kastConfig.boxCount}
          boxItems={boxItems}
          kastLabel={kastConfig.label}
          getItemById={getItemById}
        />
      </div>
    </div>
  );
};

export default KastPage;
